import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "../generic/InputField";
import MaterialTypeSelector from "../shared/MaterialTypeSelector";
import AddAnotherItemButton from "../generic/AddAnotherItemButton";
import SubmitButton from "../generic/SubmitButton";
import IDDropdown from "../generic/IDDropdown";
import { updateInventory } from "../../services/MaterialAPI";
import { defaultUpdateInventoryItem, UpdateMaterialInventoryPayload } from "../../types/InventoryItem";
import { fetchTemplateDetails, fetchTemplates } from "../../services/TemplateAPI";
import QuantityUnitDropdown from "../shared/QuantityUnitDropdown";
import SharedAlertBanner from "../shared/SharedAlertBanner";
import { catchError, handleInventoryUpdateResponseAlerts } from "../../utils/AlertUtils";
import { UserContext } from "../../context/UserContext";
import { Messages } from "../../constants/Messages";
import { TABS } from "../../constants/MainTabs";
import FormComponent from "../generic/FormComponent";
import RowItem from "../generic/RowItem"; // Import RowItem

const LABELS = {
    QUANTITY_CHANGED: "Quantity Changed",
};

const UseTemplateForm: React.FC = () => {
    const [templates, setTemplates] = useState<{ id: number; name: string }[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [rows, setRows] = useState<UpdateMaterialInventoryPayload[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [successAlert, setSuccessAlert] = useState<{ message: string } | null>(null);
    const [errorAlert, setErrorAlert] = useState<{ message: string } | null>(null);
    const { isLoggedIn } = useContext(UserContext);
    const navigate = useNavigate();

    // Fetch templates when the user logs in
    useEffect(() => {
        const fetchAvailableTemplates = async () => {
            try {
                const templates = await fetchTemplates();
                setTemplates(templates);
            } catch (error) {
                console.error("Error fetching templates:", error);
            }
        };
        fetchAvailableTemplates();
    }, [isLoggedIn]);

    // Fetch template details when a template is selected
    useEffect(() => {
        const fetchDetails = async () => {
            if (!selectedTemplateId) return;

            try {
                const response = await fetchTemplateDetails(selectedTemplateId);
                const details = response.materials;

                if (Array.isArray(details)) {
                    const formattedRows = details.map((detail: any) => ({
                        material_type_id: detail.material_type_id,
                        material_size_id: detail.material_size_id,
                        color_code: detail.color_code,
                        is_new: false,
                        quantity_changed: detail.quantity,
                        quantity_unit: detail.quantity_unit,
                    }));
                    setRows(formattedRows);
                } else {
                    throw new Error(Messages.ERROR_TEMPLATE_DETAILS_ARRAY);
                }
            } catch (error) {
                console.error("Error fetching template details:", error);
            }
        };

        fetchDetails();
    }, [selectedTemplateId]);

    const handleRowChange = (index: number, field: string, value: any) => {
        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            updatedRows[index] = { ...updatedRows[index], [field]: value };
            return updatedRows;
        });
        clearAlerts();
    };

    const clearAlerts = () => {
        setSuccessAlert(null);
        setErrorAlert(null);
    };

    const removeRow = (index: number) => {
        setRows((prevRows) => prevRows.filter((_, i) => i !== index));
    };

    const isFormValid = () =>
        rows.every((row) => row.material_type_id && row.material_size_id && row.color_code);

    const handleSubmit = async () => {
        if (!isFormValid()) {
            setErrorAlert({ message: Messages.ERROR_ALL_FIELDS_REQUIRED });
            return;
        }

        setIsLoading(true);

        try {
            const response = await updateInventory(rows);

            handleInventoryUpdateResponseAlerts(response, setSuccessAlert, setErrorAlert);

            if (response.errors && response.errors.length > 0) {
                setRows(response.errors.results); // Reset items to only include invalid items
            } else {
                // Reset form fields after successful submission
                setSelectedTemplateId(null);
                setRows([]);
            }
        } catch (error) {
            catchError(error, setErrorAlert);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FormComponent>
            <div className={"py-4"}>
            {/* Shared Alert Banner */}
                <SharedAlertBanner
                    successAlert={successAlert}
                    errorAlert={errorAlert}
                    onCloseSuccess={() => setSuccessAlert(null)}
                    onCloseError={() => setErrorAlert(null)}
                />
            </div>

            {/* Create Template Button to go to template page */}
            <button className={"primary mb-4"} onClick={() => navigate(`/${TABS[3].endpoint}`)}>
                Create Template
            </button>

            {/* Template Selector */}
            <div className="p-2">
                <IDDropdown
                    label="Select Template"
                    options={templates.map((template) => ({ value: template.id, label: template.name }))}
                    selectedValue={selectedTemplateId}
                    onChange={setSelectedTemplateId}
                    placeholder="-- Select a Template --"
                />
            </div>

            {/* Rows for template details */}
            {rows.map((row, index) => (
                <RowItem
                    key={index}
                    index={index}
                    showRemoveButton={rows.length > 1}
                    onRemove={removeRow}
                >
                    <MaterialTypeSelector
                        selectedValue={{
                            material_type_id: row.material_type_id,
                            color_code: row.color_code,
                            material_size_id: row.material_size_id,
                        }}
                        onSelect={({ material_type_id, color_code, material_size_id }) => {
                            handleRowChange(index, "material_type_id", material_type_id);
                            handleRowChange(index, "material_size_id", material_size_id);
                            handleRowChange(index, "color_code", color_code || "");
                        }}
                    />

                    <InputField
                        label={LABELS.QUANTITY_CHANGED}
                        type="number"
                        step="0.01"
                        value={row.quantity_changed || 0}
                        onChange={(value) => handleRowChange(index, "quantity_changed", Number(value))}
                        placeholder="Enter quantity changed"
                        required
                    />

                    <QuantityUnitDropdown
                        value={row.quantity_unit || null}
                        onChange={(value) => handleRowChange(index, "quantity_unit", value)}
                    />
                </RowItem>
            ))}

            {/* Buttons for adding new rows and submitting */}
            <div className="flex justify-between items-center space-x-20">
                <AddAnotherItemButton
                    onAdd={() =>
                        setRows([
                            ...rows,
                            {
                                ...defaultUpdateInventoryItem,
                            },
                        ])
                    }
                />
                <SubmitButton isLoading={isLoading} disabled={!isFormValid()} onClick={handleSubmit} />
            </div>
        </FormComponent>
    );
};

export default UseTemplateForm;
